/**
 * Форматтеры и утилиты для карточки заявки
 */

import { getStatusText as getStatusTextFallback, getStatusVariant as getStatusVariantFallback } from '../requestStatuses';

/**
 * Форматировать дату в локальный формат
 * @param {string} dateString - строка с датой
 * @returns {string} - отформатированная дата
 */
export function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);

    const datePart = date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }); // 06.02.2026

    const weekday = date
        .toLocaleDateString('ru-RU', { weekday: 'short' })
        .replace('.', '');

    const timePart = date.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit'
    }); // 14:30

    return `${datePart} ${weekday}, ${timePart}`;
}

/**
 * Форматировать длительность в короткий вид: дни, часы, минуты.
 * @param {number} ms - длительность в миллисекундах
 * @returns {string}
 */
export function formatDuration(ms) {
    const totalMinutes = Math.max(0, Math.floor((Number(ms) || 0) / 60000));
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    const parts = [];

    if (days > 0) parts.push(`${days} д`);
    if (hours > 0) parts.push(`${hours} ч`);
    if (minutes > 0 || parts.length === 0) parts.push(`${minutes} мин`);

    return parts.join(' ');
}

/**
 * Форматировать размер файла
 * @param {number} bytes - размер в байтах
 * @returns {string} - отформатированный размер
 */
export function formatFileSize(bytes) {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Получить текст статуса заявки
 * Предпочтительно использовать переводы из DTO (request.status_label)
 * @param {string} status - код статуса
 * @returns {string} - текст статуса
 */
export function getStatusText(status) {
    return getStatusTextFallback(status);
}

/**
 * Получить вариант цвета для статуса заявки
 * Предпочтительно использовать переводы из DTO (request.status_variant)
 * @param {string} status - код статуса
 * @returns {string} - вариант Bootstrap
 */
export function getStatusVariant(status) {
    return getStatusVariantFallback(status);
}

/**
 * Получить текст приоритета
 * @param {string} priority - код приоритета
 * @returns {string} - текст приоритета
 */
export function getPriorityText(priority) {
    const texts = {
        'low': 'Низкий',
        'normal': 'Обычный',
        'high': 'Высокий',
        'urgent': 'Срочный'
    };
    return texts[priority] || priority;
}

/**
 * Получить вариант цвета для приоритета
 * @param {string} priority - код приоритета
 * @returns {string} - вариант Bootstrap
 */
export function getPriorityVariant(priority) {
    const variants = {
        'low': 'secondary',
        'normal': 'info',
        'high': 'warning',
        'urgent': 'danger'
    };
    return variants[priority] || 'info';
}

/**
 * Получить текст статуса участника
 * @param {string} status - статус участника
 * @returns {string} - текст статуса
 */
export function getParticipantStatusText(status) {
    const texts = {
        'created': 'Создал',
        'pending': 'Ожидает',
        'active': 'Активен',
        'approved': 'Согласовал',
        'rejected': 'Отклонил',
        'returned': 'Вернул',
        'cancelled': 'Отменил',
        'completed': 'Завершил',
        'assigned': 'Назначен'
    };
    return texts[status] || status;
}

/**
 * Получить вариант цвета для статуса участника
 * @param {string} status - статус участника
 * @returns {string} - вариант Bootstrap
 */
export function getParticipantStatusVariant(status) {
    const variants = {
        'created': 'primary',
        'pending': 'warning',
        'active': 'primary',
        'approved': 'success',
        'rejected': 'danger',
        'returned': 'warning',
        'cancelled': 'dark',
        'completed': 'info',
        'assigned': 'success'
    };
    return variants[status] || 'secondary';
}

/**
 * Получить текст статуса назначающего
 * @param {Object} assigner - объект назначающего
 * @param {Array} executors - список исполнителей
 * @returns {string} - текст статуса
 */
export function getAssignerStatusText(assigner, executors) {
    // Проверяем, есть ли активный исполнитель, назначенный этим назначающим
    const hasActiveExecutor = executors.some(executor => {
        return executor.status === 'active' && executor.assigned_by === assigner.user_id;
    });

    if (hasActiveExecutor) {
        return 'Назначил исполнителя';
    }

    // Если нет активного исполнителя, назначенного этим назначающим, показываем стандартный статус
    return getParticipantStatusText(assigner.status);
}

/**
 * Получить вариант цвета для статуса назначающего
 * @param {Object} assigner - объект назначающего
 * @param {Array} executors - список исполнителей
 * @returns {string} - вариант Bootstrap
 */
export function getAssignerStatusVariant(assigner, executors) {
    // Проверяем, есть ли активный исполнитель, назначенный этим назначающим
    const hasActiveExecutor = executors.some(executor => {
        return executor.status === 'active' && executor.assigned_by === assigner.user_id;
    });

    if (hasActiveExecutor) {
        return 'success'; // Зеленый цвет для "Назначил исполнителя"
    }

    // Если нет активного исполнителя, показываем стандартный вариант
    return getParticipantStatusVariant(assigner.status);
}

/**
 * Получить отображаемое имя инициатора
 * @param {Object} initiator - объект инициатора
 * @param {Object} request - объект заявки
 * @returns {string} - имя инициатора
 */
export function getInitiatorDisplayName(initiator, request) {
    if (initiator && initiator.User) {
        return `${initiator.User.last_name} ${initiator.User.first_name}`;
    }
    if (request && request.Initiator) {
        return `${request.Initiator.last_name} ${request.Initiator.first_name}`;
    }
    return 'Не указан';
}

const REQUEST_HISTORY_ACTION_TEXTS = {
    // Базовые события заявки
    created: 'создал заявку',
    updated: 'обновил заявку',
    deadline_updated: 'изменил срок или срочность заявки',
    status_changed: 'изменил статус заявки',
    change_status: 'изменил статус заявки вручную',
    submitted: 'отправил заявку на согласование',
    approved: 'согласовал заявку',
    rejected: 'отклонил заявку',
    returned: 'вернул заявку на доработку',
    assigned: 'назначил исполнителя',
    execute: 'взял заявку в работу',
    completed: 'завершил выполнение заявки',
    partially_complete: 'частично выполнил заявку',
    accepted: 'подтвердил выполнение заявки',
    accept_partially: 'принял частично выполненную заявку',
    cancelled: 'отменил заявку',

    // Комментарии и файлы
    commented: 'добавил комментарий',
    comment_edited: 'отредактировал комментарий',
    comment_deleted: 'удалил комментарий',
    file_removed: 'удалил файл',
    required_attachment_added: 'добавил обязательный документ',

    // Позиции, материалы и исполнители по строкам
    item_added: 'добавил позицию',
    item_updated: 'обновил позицию',
    item_deleted: 'удалил позицию',
    item_notes_updated: 'изменил примечание к позиции',
    item_shipping_comment_updated: 'изменил комментарий отгрузки по позиции',
    materials_shipped: 'обновил отгрузку материалов',
    materials_accepted: 'обновил приемку материалов',
    fulfillment_dispute: 'сообщил о проблеме при приёмке',
    fulfillment_shipping: 'подтвердил отгрузку',
    fulfillment_acceptance: 'подтвердил приёмку',
    fulfillment_in_transit: 'сообщил, что доставка ещё в пути',
    fulfillment_review_claim: 'взялся решить вопрос',
    fulfillment_review_continue: 'решил продолжить работу',
    fulfillment_review_stop: 'решил закончить поставку',
    fulfillment_review_resolve_acceptance: 'закрыл заявку без подтверждения приёмки после разбора',
    fulfillment_close_without_acceptance: 'закрыл заявку без подтверждения приёмки по истечении срока',
    fulfillment_close_without_shipment: 'закрыл заявку без поставки',
    fulfillment_close_legacy_without_acceptance: 'закрыл старую заявку без подтверждения приёмки',
    fulfillment_explain_delivery: 'передал пояснение о завершении своей работы',
    request_quantity_shipment_adjusted: 'обновил количество техники по заявке',
    auto_status_from_shipping: 'автоматически изменил статус по факту отгрузки',
    auto_status_from_acceptance: 'автоматически изменил статус по факту приемки',
    auto_executor_completed_from_shipping: 'автоматически завершил часть исполнителя после полной отгрузки',
    auto_executor_reactivated_from_shipping: 'автоматически вернул часть исполнителя в работу после изменения отгрузки',
    auto_closed_acceptance_timeout: 'автоматически завершила заявку',
    multi_executor_enabled: 'включил режим нескольких исполнителей',
    multi_executor_auto_enabled: 'включил режим нескольких исполнителей автоматически',
    multi_executor_self_assign_mode_enabled: 'включил построчное выполнение',
    item_executor_assigned: 'назначил исполнителя на позицию',
    item_executor_reassigned: 'переназначил исполнителя позиции',
    item_executor_self_assigned: 'взял позицию на себя',
    item_executor_self_released: 'снял самоназначение со строки',
    item_executor_self_assign_bulk: 'массово взял свободные строки на себя',

    // Участники маршрута
    participant_added: 'добавил участника процесса',
    participant_replaced: 'заменил участника процесса',
    participant_removed: 'удалил участника процесса',
    initiator_replaced: 'заменил инициатора',
    approver_added: 'добавил согласующего',
    approver_replaced: 'заменил согласующего',
    approver_removed: 'удалил согласующего',
    add: 'добавил участника процесса',
    replace: 'заменил участника процесса',
    remove: 'удалил участника процесса',

    // Связанные заявки и закупки
    linked_request_based_on: 'создал заявку на основании связанной заявки',
    linked_request_created: 'создал связанную заявку',
    quote_requested: 'запросил коммерческое предложение',

    // Топливные карты
    fuel_card_selected: 'выбрал топливную карту',
    fuel_card_target_object_selected: 'выбрал объект для топливной карты',
    fuel_card_limits_updated: 'обновил лимиты топливной карты',
    request_assign_to_initiator: 'выдал топливную карту инициатору заявки',
    request_assign_to_action_user: 'выдал топливную карту пользователю, выполнившему действие',
    request_issue_to_recipient: 'выдал топливную карту получателю из заявки',
    request_move_to_recipient: 'переместил топливную карту получателю из заявки',
    request_change_limits: 'изменил суточные лимиты топливной карты',
    request_attach_to_request_object: 'привязал топливную карту к объекту заявки',
    request_return_card: 'зафиксировал сдачу топливной карты',
    request_block_card: 'заблокировал топливную карту',
    request_mark_lost: 'зафиксировал утерю топливной карты',

    // Техника и перемещения
    mechanic_asset_received: 'оприходовал позицию в справочник механиков',
    mechanic_item_fulfillments_updated: 'обновил отгрузку техники',
    mechanic_asset_transfer_started: 'начал перемещение техники',
    mechanic_asset_transfer_cancelled: 'отменил перемещение техники',
    request_transfer_started: 'начал перемещение техники по заявке',
    request_transfer_updated: 'обновил перемещение техники по заявке',
    request_transfer_cancelled: 'отменил перемещение техники по заявке',
    request_issue_to_object: 'выдал технику на объект по заявке',
    request_move_to_object: 'переместил технику на объект по заявке',
    request_move_to_warehouse: 'переместил технику на склад по заявке',
    request_return_to_warehouse: 'вернул технику на склад по заявке',
    request_accept_to_repair: 'принял технику в ремонт по заявке',
    request_finish_repair: 'завершил ремонт техники по заявке',
    request_rent_out: 'сдал технику в аренду по заявке',
    request_return_from_rent: 'вернул технику из аренды по заявке',
    request_repair_created: 'создал заявку на ремонт техники',
    request_repair_taken: 'взял ремонт техники в работу',
    request_repair_cancelled: 'отменил ремонт техники'
};

function getUnknownHistoryActionText(action) {
    if (!action) {
        return 'выполнил действие по заявке';
    }

    if (action.indexOf('fuel_card_') === 0) {
        return 'обновил топливную карту';
    }
    if (action.indexOf('mechanic_') === 0) {
        return 'обновил данные техники';
    }
    if (action.indexOf('request_') === 0) {
        return 'выполнил действие по заявке';
    }
    if (action.indexOf('item_') === 0) {
        return 'обновил позицию';
    }
    if (action.indexOf('participant_') === 0 || action.indexOf('approver_') === 0) {
        return 'изменил участников заявки';
    }
    if (action.indexOf('file_') === 0) {
        return 'обновил файлы заявки';
    }

    return 'выполнил действие по заявке';
}

/**
 * Получить текст действия в истории
 * @param {string} action - код действия
 * @returns {string} - текст действия
 */
export function getHistoryActionText(action) {
    return REQUEST_HISTORY_ACTION_TEXTS[action] || getUnknownHistoryActionText(action);
}

/**
 * Получить текст роли участника
 * @param {string} role - код роли
 * @returns {string} - текст роли
 */
export function getParticipantRoleText(role) {
    const texts = {
        'initiator': 'Инициатор',
        'approver': 'Согласующий',
        'executor': 'Исполнитель',
        'observer': 'Наблюдатель',
        'assigner': 'Назначающий'
    };
    return texts[role] || role;
}

export default {
    formatDate,
    formatFileSize,
    getStatusText,
    getStatusVariant,
    getPriorityText,
    getPriorityVariant,
    getParticipantStatusText,
    getParticipantStatusVariant,
    getAssignerStatusText,
    getAssignerStatusVariant,
    getInitiatorDisplayName,
    getHistoryActionText,
    getParticipantRoleText
};
